import { CompactText } from 'ui/CompactText';
import type { FC } from 'react';
import { PageHead } from 'ui/kit/PageHead';
import { NavLink } from 'react-router';

import { useVerificationEdits, type RejectedVerificationItem } from 'hooks/useVerificationEdits';

import styles from './VerificationEdits.module.scss';
import { BrandLoader } from 'ui/BrandLoader/BrandLoader';

const ITEM_LABELS: Record<string, string> = {
  profile_photo: 'Фото профиля',
  profile_data: 'О себе',
};

const itemTitle = (item: RejectedVerificationItem) => {
  if (item.kind === 'education') {
    const index = item.education_index ?? 0;
    return `Период обучения №${index + 1}`;
  }
  return ITEM_LABELS[item.kind] ?? item.kind;
};

export type VerificationEditsProps = {
  editPath: string;
};

export const VerificationEdits: FC<VerificationEditsProps> = ({ editPath }) => {
  const { isLoading, isError, rejectedItems, verificationComment, profileStatus } =
    useVerificationEdits();
  /* Одобренная анкета без замечаний: не показываем «Нужны изменения» и «Закрыты до одобрения». */
  const isApprovedClean =
    !isLoading && !isError && profileStatus === 'approved' && rejectedItems.length === 0;

  return (
    <main className={styles.page}>
      <PageHead crumb={<>Личный кабинет / Мой профиль</>} title={<>{isApprovedClean ? 'Анкета одобрена' : 'Нужны изменения'}</>} />

      {isLoading ? <BrandLoader center label='Загрузка...' /> : null}
      {isError ? (
        <p className={styles.fact}>Не удалось загрузить статус модерации. Попробуйте позже.</p>
      ) : null}

      {!isLoading && !isError ? (
        <section className={styles.card}>
          <h2 className={styles.cardTitle}>Комментарий к анкете</h2>
          <p className={styles.fact}>
            {verificationComment?.trim() ||
              (rejectedItems.length
                ? 'Исправьте отмеченные поля, затем отправьте анкету снова.'
                : profileStatus === 'approved'
                  ? 'Нет полей, ожидающих исправления.'
                  : 'Модератор пока не оставил замечаний по отдельным полям.')}
          </p>
          {rejectedItems.map((item) => (
            <div key={item.key} className={styles.note}>
              <p className={styles.noteTitle}>{itemTitle(item)}</p>
              <CompactText className={styles.noteText} tone={'info'}>
                {item.comment?.trim() || 'Нужна правка этого поля.'}
              </CompactText>
            </div>
          ))}
        </section>
      ) : null}

      {!isLoading && !isError ? (
        <section className={styles.card}>
          <h2 className={styles.cardTitle}>
            {isApprovedClean ? 'Доступ' : 'После исправлений'}
          </h2>
          <div className={styles.row}>
            <p className={styles.rowLabel}>Поиск и чаты</p>
            <p className={styles.rowValue}>
              {isApprovedClean ? 'Открыты' : 'Закрыты до одобрения'}
            </p>
          </div>
          <CompactText className={styles.noteText} tone={'info'}>
            {isApprovedClean
              ? 'Анкета прошла проверку. Новые правки в анкете снова уйдут модератору.'
              : 'Сохраните правки и повторно отправьте профиль на проверку. Изменение полей само по себе не открывает доступ.'}
          </CompactText>
          <div className={styles.actions}>
            {isApprovedClean ? null : (
              <NavLink to={editPath} className={styles.primary}>
                Сохранить исправления
              </NavLink>
            )}
            <NavLink to={editPath} className={styles.ghost}>
              К анкете
            </NavLink>
          </div>
        </section>
      ) : null}
    </main>
  );
};
